import React from 'react'
import { Control, Controller, FieldValues } from 'react-hook-form'
import TextField from '@mui/material/TextField'
import { InputProps } from '../../../store/types'
import { Grid, InputAdornment, Typography } from '@mui/material'
import { t } from 'i18next'

type TextInputProps = {
  maxLength?: number
  required?: boolean
  minValue?: number
  maxValue?: number
  numericType?: NumericType
  unitOfMeasure?: string
  multiline?: boolean
  rows?: number
  showCharCounter?: boolean
  decimalPrecision?: number
}
type NumericType = 'natural' | 'decimal' | 'integer' | 'float' | undefined

const validateTypeOnChange = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  onChange: (...event: any[]) => void,
  numericType: NumericType,
  precision?: number,
  name?: string
) => {
  let value = e.target.value

  if (numericType === 'natural') {
    value = value.replace(/\D/g, '').replace(/^0+(?!$)/, '')
  }
  if (numericType === 'decimal') {
    value = value.replace(/[^0-9.]/g, '')
    const parts = value.split('.')
    if (parts.length > 2) {
      value = `${parts[0]}.${parts.slice(1).join('')}`
    }
    if (parts[1]?.length > (precision ? precision : 2)) {
      value = `${parts[0]}.${parts[1].slice(0, precision ? precision : 2)}`
    }
  }
  if (numericType === 'integer') {
    value = value.replace(/[^0-9-]/g, '')
  }
  if (numericType === 'float') {
    value = value.replace(/[^0-9.-]/g, '')
  }

  if (name === t('page.worklog.form.timeSpent')) {
    value = value.replace(',', '.')

    const decimalMatch = value.match(/^(\d+)\.5$/)
    if (decimalMatch) {
      const hours = decimalMatch[1]
      if (hours !== '0') {
        value = `${hours}h 30m`
      } else {
        value = `30m`
      }
    } else {
      if (/\d\s$/.test(value) && !/h/.test(value)) {
        value = value.trimEnd() + 'h '
      }
    }

    if (/h\s{2,}/.test(value)) {
      value = value.replace(/h\s{2,}/g, 'h ')
    }

    if (/h\s\d+\s$/.test(value)) {
      value = value.trimEnd() + 'm'
    }

    if (/m\s*\d*h/.test(value)) {
      value = value.replace(/m\s*\d*h/g, 'm')
    }
  }

  onChange(value)
}

const handleBlur = (value: string, onChange: (...event: any[]) => void, label: string) => {
  if (label === t('page.worklog.form.timeSpent')) {
    if (/^\d+$/.test(value)) {
      value += 'h'
    }
    if (/\d$/.test(value) && !value.includes('h')) {
      value += 'h'
    }

    if (/^\d+h \d+$/.test(value)) {
      value += 'm'
    }
    onChange(value)
  }
}

const TextInput = <T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  rules,
  maxLength,
  required = false,
  disabled,
  minValue,
  maxValue,
  numericType,
  unitOfMeasure,
  multiline,
  rows,
  showCharCounter,
  decimalPrecision,
  ...textFieldProps
}: InputProps<T> & TextInputProps) => {
  rules = {
    ...rules,
    validate: {
      ...rules?.validate,
      notJustWhitespace: value => {
        if (typeof value === 'string' && value.trim() === '' && value !== '') {
          return t('page.usersProfile.form.errors.onlyWhitespaceNotAccepted')
        }
        return true
      },
    },
  }
  return (
    <Controller
      name={name}
      control={control as Control<T>}
      rules={rules}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <TextField
          id={`text-input-${name}`}
          aria-label={label}
          required={required}
          size="small"
          placeholder={placeholder}
          error={!!error}
          onChange={e => {
            validateTypeOnChange(e, onChange, numericType as NumericType, decimalPrecision, label)
          }}
          onBlur={() => handleBlur(value ?? '', onChange, label)}
          value={value === null || value === undefined ? '' : value}
          disabled={disabled}
          fullWidth
          label={label}
          variant="outlined"
          multiline={multiline}
          rows={rows}
          {...textFieldProps}
          inputProps={{ min: minValue, max: maxValue, maxLength: maxLength }}
          InputProps={{
            endAdornment: unitOfMeasure ? <InputAdornment position="end">{unitOfMeasure}</InputAdornment> : null,
          }}
          helperText={
            error || showCharCounter ? (
              <Grid component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="caption">{error ? error.message : null}</Typography>
                {showCharCounter && (
                  <Typography variant="caption">
                    {(value ?? '').toString().length}/{maxLength}
                  </Typography>
                )}
              </Grid>
            ) : null
          }
        />
      )}
    />
  )
}

export default TextInput
